import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, unref as _unref, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "btn" }

import { computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'

import { PaginationParams } from '@/models/api'
import { Trip, TripStatus } from '@/models/trip'

import { GetTripsParams, useTripsStore } from '@/store/trips'

import { formatDateWithTime } from '@/helpers/date'
import { commonCase } from '@/helpers/strings'
import { showToast } from '@/helpers/messages'
import { normalizeUserName } from '@/helpers/user'
import { normalizeCarName } from '@/helpers/car-profile'

import useQueryParams from '@/composables/useQueryParams'

import CommonError from '@/components/CommonError.vue'
import Loader from '@/components/Loader.vue'
import { Ref, ref } from 'vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'List',
  props: {
    query: {}
  },
  setup(__props: any) {

const props = __props

const tripsStore = useTripsStore()

const router = useRouter()

const isLoading = computed(() => tripsStore.listModule.isLoading)
const isInitialLoadingDone = computed(
  () => tripsStore.listModule.isInitialLoadingDone
)
const error = computed(() => tripsStore.listModule.error)
const list = computed(() => tripsStore.listModule.list)
const meta = computed(() => tripsStore.listModule.meta)

// A failed reload keeps the already loaded data and only shows a toast.
watch(error, e => {
  if (e && list.value.length) showToast({ type: 'error', message: e })
})

const currentStatus = ref<TripStatus | undefined>(undefined)
const searchTerm: Ref<string> = ref('')

const getTrips = (params: PaginationParams & GetTripsParams) => {
  tripsStore.getTrips(params)
}

const params = useQueryParams<PaginationParams & GetTripsParams>({
  ...props.query,
})

const filteredTripsList = computed(() => {
  let filteredList = list.value

  if (searchTerm.value) {
    const lowerCaseSearchTerms = searchTerm.value.toLowerCase().split(' ')
    filteredList = filteredList.filter((trip: any) => {
      const hostFirstName = trip.host?.first_name?.toLowerCase() || ''
      const hostLastName = trip.host?.last_name?.toLowerCase() || ''
      const renterFirstName = trip.renter?.first_name?.toLowerCase() || ''
      const renterLastName = trip.renter?.last_name?.toLowerCase() || ''
      return lowerCaseSearchTerms.every(
        term =>
          hostFirstName.startsWith(term) ||
          hostLastName.startsWith(term) ||
          renterFirstName.startsWith(term) ||
          renterLastName.startsWith(term)
      )
    })
  }

  return filteredList
})

const setFilter = (status: TripStatus | undefined) => {
  currentStatus.value = status
  params.statuses = status ? [status] : undefined
  params.page = 1
}

onMounted(() => {
  // Initialize currentStatus based on params if present (optional, but good practice)
  if (params.statuses && params.statuses.length === 1) {
    currentStatus.value = params.statuses[0]
  }
  getTrips(params)
})

watch(
  () => props.query,
  updated => {
    Object.assign(params, updated)
    if (updated.statuses && updated.statuses.length === 1) {
      currentStatus.value = updated.statuses[0]
    } else {
      currentStatus.value = undefined
    }
  },
  { deep: true }
)

watch(params, updatedParams => {
  getTrips(updatedParams)
})

const onRetryPress = () => {
  getTrips(params)
}

const handleRowClick = (trip: Trip) => {
  router.push(`/trips/${trip.id}`)
}

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_scrollbar = _resolveComponent("el-scrollbar")!
  const _directive_loading = _resolveDirective("loading")!

  return (error.value && !list.value.length)
    ? (_openBlock(), _createBlock(CommonError, {
        key: 0,
        errorMessage: error.value,
        onRetryPress: onRetryPress
      }, null, 8, ["errorMessage"]))
    : (!isInitialLoadingDone.value || !meta.value)
      ? (_openBlock(), _createBlock(Loader, { key: 1 }))
      : (_openBlock(), _createBlock(_component_el_scrollbar, {
          key: 2,
          always: ""
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", null, [
              _createVNode(_component_el_input, {
                modelValue: searchTerm.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
                placeholder: "Search by first name or last name",
                class: "input-margin"
              }, null, 8, ["modelValue"]),
              _cache[12] || (_cache[12] = _createElementVNode("br", null, null, -1)),
              _createElementVNode("div", _hoisted_1, [
                _createVNode(_component_el_button, {
                  type: !currentStatus.value ? 'primary' : 'default',
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (setFilter(undefined)))
                }, {
                  default: _withCtx(() => _cache[7] || (_cache[7] = [
                    _createTextVNode(" All trips ")
                  ])),
                  _: 1,
                  __: [7]
                }, 8, ["type"]),
                _createVNode(_component_el_button, {
                  type: currentStatus.value === _unref(TripStatus).PENDING ? 'primary' : 'default',
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (setFilter(_unref(TripStatus).PENDING)))
                }, {
                  default: _withCtx(() => _cache[8] || (_cache[8] = [
                    _createTextVNode(" Pending ")
                  ])),
                  _: 1,
                  __: [8]
                }, 8, ["type"]),
                _createVNode(_component_el_button, {
                  type: currentStatus.value === _unref(TripStatus).ONGOING ? 'primary' : 'default',
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (setFilter(_unref(TripStatus).ONGOING)))
                }, {
                  default: _withCtx(() => _cache[9] || (_cache[9] = [
                    _createTextVNode(" Ongoing ")
                  ])),
                  _: 1,
                  __: [9]
                }, 8, ["type"]),
                _createVNode(_component_el_button, {
                  type: currentStatus.value === _unref(TripStatus).FINISHED ? 'primary' : 'default',
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (setFilter(_unref(TripStatus).FINISHED)))
                }, {
                  default: _withCtx(() => _cache[10] || (_cache[10] = [
                    _createTextVNode(" Finished ")
                  ])),
                  _: 1,
                  __: [10]
                }, 8, ["type"]),
                _createVNode(_component_el_button, {
                  type: currentStatus.value === _unref(TripStatus).ON_HOLD ? 'primary' : 'default',
                  onClick: _cache[5] || (_cache[5] = ($event: any) => (setFilter(_unref(TripStatus).ON_HOLD)))
                }, {
                  default: _withCtx(() => _cache[11] || (_cache[11] = [
                    _createTextVNode(" On Hold ")
                  ])),
                  _: 1,
                  __: [11]
                }, 8, ["type"])
              ])
            ]),
            _createVNode(_component_el_row, null, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 24 }, {
                  default: _withCtx(() => [
                    _withDirectives((_openBlock(), _createBlock(_component_el_table, {
                      style: {"width":"100%"},
                      data: filteredTripsList.value,
                      onRowClick: handleRowClick,
                      "element-loading-background": "rgba(255, 255, 255, 0.5)"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_table_column, {
                          prop: "id",
                          label: "Id"
                        }),
                        _createVNode(_component_el_table_column, {
                          prop: "status",
                          label: "Status"
                        }, {
                          default: _withCtx((scope) => [
                            _createTextVNode(_toDisplayString(_unref(commonCase)(scope.row.status)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, { label: "Host" }, {
                          default: _withCtx((scope) => [
                            _createTextVNode(_toDisplayString(_unref(normalizeUserName)(scope.row.host)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, { label: "Vehicle info" }, {
                          default: _withCtx((scope) => [
                            _createTextVNode(_toDisplayString(_unref(normalizeCarName)(scope.row.car_profile)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, { label: "Renter" }, {
                          default: _withCtx((scope) => [
                            _createTextVNode(_toDisplayString(_unref(normalizeUserName)(scope.row.renter)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, {
                          prop: "start_date",
                          label: "Start date"
                        }, {
                          default: _withCtx(({ row }) => [
                            _createTextVNode(_toDisplayString(_unref(formatDateWithTime)(row.start_date)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, {
                          prop: "end_date",
                          label: "End date"
                        }, {
                          default: _withCtx(({ row }) => [
                            _createTextVNode(_toDisplayString(_unref(formatDateWithTime)(row.end_date)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, { width: "120px" }, {
                          default: _withCtx((scope) => [
                            _createVNode(_component_el_button, {
                              plain: "",
                              type: "primary",
                              size: "small",
                              class: "test-btn",
                              icon: "el-icon-view",
                              onClick: ($event: any) => (handleRowClick(scope.row))
                            }, {
                              default: _withCtx(() => _cache[13] || (_cache[13] = [
                                _createTextVNode(" Show ")
                              ])),
                              _: 2,
                              __: [13]
                            }, 1032, ["onClick"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["data"])), [
                      [_directive_loading, isLoading.value]
                    ]),
                    _createVNode(_component_el_pagination, {
                      background: "",
                      layout: "prev, pager, next",
                      total: meta.value.total,
                      disabled: isLoading.value,
                      "page-size": meta.value.perPage,
                      currentPage: _unref(params).page,
                      "onUpdate:currentPage": _cache[6] || (_cache[6] = ($event: any) => ((_unref(params).page) = $event))
                    }, null, 8, ["total", "disabled", "page-size", "currentPage"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
}
}

})